import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ErrorHandler from '../../components/ErrorHandler';
import { logger } from '../../utils/logger';
import { scrollToTop } from '../../utils/scrollHelper';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Get the redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';
  
  // Check for success message from other pages (like confirmation)
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
    
    // Check for error message from ProtectedRoute redirect
    if (location.state?.error) {
      setErrors(prev => ({
        ...prev,
        general: location.state.error,
        errorTitle: location.state.errorTitle || 'Error'
      }));
      // Clear the error from location state
      window.history.replaceState({}, document.title);
    }
    
    // Check for expired session parameter
    const queryParams = new URLSearchParams(location.search);
    const expired = queryParams.get('expired');
    
    if (expired === 'true') {
      setErrors(prev => ({
        ...prev,
        general: 'Your session has expired. Please sign in again.',
        errorTitle: 'Session Expired'
      }));
      // Clear the query parameter
      navigate('/login', { replace: true });
    }
  }, [location.state, location.search, navigate]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      setSuccessMessage('');
      
      try {
        logger.debug('Attempting to sign in with:', formData.email);
        
        // Add a small delay to ensure the console logs are visible
        await new Promise(resolve => setTimeout(resolve, 100));
        
        await signIn(formData.email.trim(), formData.password);
        logger.debug('Sign in successful');
        
        // Always navigate to the dashboard or requested page on successful login
        navigate(from, { replace: true });
      } catch (error) {
        logger.error('Login error:', error);
        
        // Handle specific Cognito error messages
        if (error.code === 'NotAuthorizedException') {
          setErrors({
            general: 'Incorrect email or password.'
          });
        } else if (error.code === 'UserNotFoundException') {
          setErrors({
            general: 'No account found with this email address.'
          });
        } else {
          setErrors({
            general: error.message || 'Failed to sign in. Please check your credentials.'
          });
        }
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  return (
    <section className="py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="text-center mb-4">
                  <h2 className="text-primary" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>Dozzy.au</h2>
                  <h3>Welcome Back</h3>
                  <p className="text-muted">Sign in to access your account</p>
                </div>
                
                {successMessage && (
                  <div className="alert alert-success mb-4" role="alert">
                    {successMessage}
                  </div>
                )}
                
                {errors.general && (
                  <ErrorHandler 
                    error={{ 
                      general: errors.general, 
                      errorTitle: errors.errorTitle || 'Error' 
                    }} 
                    clearError={() => setErrors({...errors, general: '', errorTitle: ''})} 
                  />
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label fw-semibold">Email</label>
                    <input
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                  </div>
                  
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <label htmlFor="password" className="form-label fw-semibold">Password</label>
                      <a href="/forgot-password" onClick={(e) => {
                        e.preventDefault();
                        scrollToTop();
                        navigate('/forgot-password');
                      }} className="text-decoration-none small">Forgot password?</a>
                    </div>
                    <input
                      type="password"
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder=""
                    />
                    {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                  </div>
                  
                  <button 
                    type="submit" 
                    className="btn btn-red w-100" 
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing in...
                      </>
                    ) : 'Sign In'}
                  </button>
                </form>
                
                <div className="text-center mt-4">
                  <p className="mb-0">
                    Don't have an account? <a href="/signup" onClick={(e) => {
                      e.preventDefault();
                      scrollToTop();
                      navigate('/signup');
                    }} className="text-decoration-none fw-semibold">Sign up</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;
